import { Type, type Static } from "@sinclair/typebox";
const strict = { additionalProperties: false } as const;
const uuid = Type.String({ format: "uuid" });
const text = (maxLength: number) => Type.String({ maxLength });
const nullableUuid = Type.Union([uuid, Type.Null()]);
export const ReportSectionsSchema = Type.Object(
  {
    premise: text(4000),
    observedFacts: text(12000),
    thirdPartyInformation: text(6000),
    probableCauses: text(6000),
    recommendedChecks: text(6000),
    completedWorks: text(10000),
    recommendedWorks: text(10000),
    limitations: text(4000),
  },
  strict,
);
export const ReportPhotoSchema = Type.Object(
  {
    assetId: uuid,
    selected: Type.Boolean(),
    caption: text(1000),
    category: Type.Union([
      Type.Literal("ordinary"),
      Type.Literal("thermal"),
      Type.Literal("gauge"),
      Type.Literal("demolition"),
      Type.Literal("restoration"),
    ]),
    phase: Type.Union([
      Type.Literal("initial"),
      Type.Literal("investigation"),
      Type.Literal("work"),
      Type.Literal("final"),
    ]),
    rotation: Type.Optional(
      Type.Union([
        Type.Literal(0),
        Type.Literal(90),
        Type.Literal(180),
        Type.Literal(270),
      ]),
    ),
  },
  strict,
);
export const ReportContentSchema = Type.Object(
  {
    clientName: text(300),
    address: text(600),
    administrator: text(300),
    administratorId: Type.Optional(nullableUuid),
    condominiumId: Type.Optional(nullableUuid),
    recipient: text(300),
    references: text(1000),
    subject: Type.String({ minLength: 1, maxLength: 500, pattern: "\\S" }),
    inspectedOn: Type.Union([Type.String({ format: "date" }), Type.Null()]),
    technicalText: text(24000),
    reportType: Type.Optional(
      Type.Union([Type.Literal("inspection"), Type.Literal("intervention")]),
    ),
    draftedOn: Type.Optional(Type.String({ format: "date" })),
    inspectionDates: Type.Optional(
      Type.Array(Type.String({ format: "date" }), {
        maxItems: 30,
        uniqueItems: true,
      }),
    ),
    inspectionNotApplicable: Type.Optional(Type.Boolean()),
    technician: Type.Optional(text(300)),
    technicianId: Type.Optional(nullableUuid),
    unitsAndPeople: Type.Optional(text(1500)),
    sections: Type.Optional(ReportSectionsSchema),
    photos: Type.Optional(Type.Array(ReportPhotoSchema, { maxItems: 30 })),
    photosPerPage: Type.Optional(
      Type.Union([Type.Literal(1), Type.Literal(2), Type.Literal(4)]),
    ),
    photoLimitOverride: Type.Optional(Type.Boolean()),
    photoOverrideReason: Type.Optional(text(500)),
  },
  strict,
);
export type ReportContent = Static<typeof ReportContentSchema>;
export interface ReportDirectory {
  administrators: { id: string; name: string }[];
  condominiums: {
    id: string;
    name: string;
    administratorId: string | null;
    address: string;
  }[];
  technicians: { id: string; name: string }[];
}
const identity = { id: uuid, version: Type.Integer({ minimum: 0 }) };
export const ReportSaveSchema = Type.Object(
  {
    kind: Type.Literal("report.save"),
    ...identity,
    content: ReportContentSchema,
    jobId: Type.Optional(nullableUuid),
    jobVersion: Type.Optional(Type.Integer({ minimum: 1 })),
  },
  strict,
);
export const ReportLinkSchema = Type.Object(
  {
    kind: Type.Literal("report.link"),
    id: uuid,
    version: Type.Integer({ minimum: 1 }),
    jobId: nullableUuid,
    jobVersion: Type.Optional(Type.Integer({ minimum: 1 })),
  },
  strict,
);
export const ReportReviewSchema = Type.Object(
  {
    allPagesViewed: Type.Literal(true),
    factsAndDatesChecked: Type.Literal(true),
    technicalClaimsSupported: Type.Literal(true),
    photosAndCaptionsChecked: Type.Literal(true),
    layoutChecked: Type.Literal(true),
  },
  strict,
);
export const ReportReviseSchema = Type.Object(
  {
    kind: Type.Literal("report.revise"),
    id: uuid,
    version: Type.Integer({ minimum: 1 }),
  },
  strict,
);
export const ReportIssueSchema = Type.Object(
  {
    kind: Type.Literal("report.issue"),
    id: uuid,
    version: Type.Integer({ minimum: 1 }),
    previewId: Type.Optional(uuid),
    review: Type.Optional(ReportReviewSchema),
  },
  strict,
);
export const ReportArchiveSchema = Type.Object(
  {
    kind: Type.Literal("report.archive"),
    id: uuid,
    version: Type.Integer({ minimum: 1 }),
    archived: Type.Boolean(),
  },
  strict,
);
export const ReportChangeSchema = Type.Union([
  ReportSaveSchema,
  ReportLinkSchema,
  ReportIssueSchema,
  ReportArchiveSchema,
  ReportReviseSchema,
]);
export type ReportChange = Static<typeof ReportChangeSchema>;
export const ReportWriteSchema = Type.Object(
  {
    operationId: uuid,
    confirmed: Type.Literal(true),
    change: ReportChangeSchema,
  },
  strict,
);
export const ReportSearchSchema = Type.Object(
  {
    search: Type.Optional(text(200)),
    jobId: Type.Optional(uuid),
    unlinked: Type.Optional(Type.Boolean()),
    archived: Type.Optional(Type.Boolean()),
    status: Type.Optional(
      Type.Union([Type.Literal("draft"), Type.Literal("issued")]),
    ),
    createdFrom: Type.Optional(Type.String({ format: "date" })),
    createdTo: Type.Optional(Type.String({ format: "date" })),
    offset: Type.Optional(
      Type.Integer({ minimum: 0, maximum: 100000, default: 0 }),
    ),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 25, default: 10 }),
    ),
  },
  strict,
);
export type ReportSearch = Static<typeof ReportSearchSchema>;
export interface ReportJob {
  id: string;
  code: string;
  managementYear: number;
  title: string;
  version: number;
}
export interface ReportAsset {
  id: string;
  name: string;
  mimeType: string;
  caption: string;
  kind: "photo" | "attachment";
  size: number;
  sha256: string;
  createdAt: string;
}
export interface ReportHistory {
  id: string;
  action: string;
  actorId: string;
  actorName: string;
  occurredAt: string;
  previousJob: ReportJob | null;
  newJob: ReportJob | null;
}
export interface TechnicalReport {
  id: string;
  number: string;
  version: number;
  status: "draft" | "issued";
  jobId: string | null;
  linkedJob: ReportJob | null;
  content: ReportContent;
  createdAt: string;
  updatedAt: string;
  issuedAt: string | null;
  archivedAt: string | null;
  assets: ReportAsset[];
  history: ReportHistory[];
  revision?: number;
  revisions?: ReportRevisionSummary[];
}
export type ReportSummary = Omit<
  TechnicalReport,
  "assets" | "history" | "content"
> & {
  clientName: string;
  subject: string;
  inspectedOn: string | null;
};
export const ReportAssetUploadSchema = Type.Object(
  {
    operationId: uuid,
    id: uuid,
    version: Type.Integer({ minimum: 1 }),
    kind: Type.Union([Type.Literal("photo"), Type.Literal("attachment")]),
    name: Type.String({ minLength: 1, maxLength: 200, pattern: "\\S" }),
    caption: text(1000),
    mimeType: Type.Union([
      Type.Literal("image/jpeg"),
      Type.Literal("image/png"),
      Type.Literal("application/pdf"),
      Type.Literal(
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ),
    ]),
    base64: Type.String({
      minLength: 4,
      maxLength: 11184812,
      pattern: "^[A-Za-z0-9+/]+={0,2}$",
    }),
  },
  strict,
);
export type ReportAssetUpload = Static<typeof ReportAssetUploadSchema>;

export interface ReportRevisionSummary {
  revision: number;
  issuedAt: string;
  issuedBy: string;
  templateId: string | null;
  templateVersion: number | null;
}
export interface ReportPreview {
  id: string;
  reportId: string;
  reportVersion: number;
  revision: number;
  pageCount: number;
  photoPages: number;
  templateId: string;
  templateVersion: number;
  warnings: string[];
  createdAt: string;
  pdfSha256: string;
}
export const ReportPreviewRequestSchema = Type.Object(
  { id: uuid, version: Type.Integer({ minimum: 1 }) },
  strict,
);

export function reorderReportPhotos(
  content: ReportContent,
  photos: NonNullable<ReportContent["photos"]>,
): ReportContent {
  const previous = (content.photos ?? []).filter((p) => p.selected),
    selected = photos.filter((p) => p.selected);
  const update = (text: string) =>
    text.replace(/\bFoto\s+(\d+)\b/giu, (original, number: string) => {
      const old = previous[Number(number) - 1];
      if (!old) return original;
      const index = selected.findIndex((p) => p.assetId === old.assetId);
      return index < 0
        ? "[riferimento fotografico da aggiornare]"
        : "Foto " + (index + 1);
    });
  return {
    ...content,
    photos,
    technicalText: update(content.technicalText),
    ...(content.sections
      ? {
          sections: Object.fromEntries(
            Object.entries(content.sections).map(([key, value]) => [
              key,
              update(value),
            ]),
          ) as NonNullable<ReportContent["sections"]>,
        }
      : {}),
  };
}
